<template>
    <header class="border-b">
        <nuxt-link
            v-if="!isLightbox"
            class="text-button flex h-10 items-center justify-center bg-primary-500 py-2 text-lg font-medium uppercase hover:underline"
            :to="organizationContext.topBarLink"
        >
            {{ organizationContext.topBarText }}
        </nuxt-link>

        <div
            class="relative flex flex-col justify-center overflow-hidden bg-white"
        >
            <div>
                <div
                    class="relative mx-0 flex items-center justify-center gap-4 px-4 py-6 md:px-7 lg:static lg:px-16 lg:py-6"
                >
                    <div class="flex items-center gap-2">
                        <img
                            :src="organization.logo"
                            :alt="organization.longName"
                            class="max-h-12"
                        />
                    </div>
                </div>
            </div>
        </div>
    </header>

    <div class="flex flex-1 flex-col">
        <div
            class="z-2 mx-0 my-2 flex-1 px-4 py-4 md:mx-6 md:my-6 lg:mx-8 lg:px-8"
        >
            <div class="mx-auto flex max-w-screen-xl items-start gap-8">
                <div class="w-full text-center text-xl">
                    {{ t('cartSidebar.empty') }}
                </div>
            </div>
        </div>

        <powered-by-footer />
    </div>
</template>

<script setup lang="ts">
import { definePageMeta, useLayoutMode } from '#imports'
import { useQuery } from '@tanstack/vue-query'
import { graphql } from '~/resources/graphql'
import { computed, onBeforeUnmount, ref } from 'vue'
import NuxtLink from '#app/components/nuxt-link'
import { useHead } from 'unhead'
import PoweredByFooter from '~/components/PoweredByFooter.vue'
import { useRpQuery } from '~/composables/graphql'
import { provideViewer } from '~/composables/use-login'
import { useRequestURL } from '#app'
import { useI18n } from 'vue-i18n'
import { useOrganizationContext } from '~/composables/use-organization-context'

definePageMeta({
    layout: false
})

const { t } = useI18n({ useScope: 'global' })
const organizationContext = useOrganizationContext()

const url = useRequestURL()
const query = useRpQuery({ orgLevel: true })
// TODO: when there are more than 100 facilities
// TODO: Limit to active facilities
const { data, suspense } = useQuery({
    queryKey: ['EmptyCartQuery', url.hostname],
    queryFn: () =>
        query(
            graphql(/* GraphQL */ `
                query EmptyCartQuery {
                    organization {
                        code
                        shortName
                        longName
                        logo
                        branding {
                            styleString
                            headLinks
                        }
                    }
                }
            `)
        )
})

const organization = computed(() => data.value?.organization)

await Promise.all([suspense(), provideViewer()])

const unmount = ref(false)
const headStyle = computed(() => {
    if (unmount.value) {
        return []
    }

    // Organization branding defines the CSS variables used by the top bar.
    return organization.value?.branding.styleString
})

const headLinks = computed(() => {
    if (!organization.value) {
        return []
    }

    return organization.value.branding.headLinks
})

useHead({
    title: organization.value?.longName,
    style: headStyle,
    link: headLinks
})

const { isLightbox } = useLayoutMode()

onBeforeUnmount(() => {
    // Clear organization branding when this standalone page is left.
    unmount.value = true
})
</script>
